import { MutableRefObject } from 'react'

import { F0Box } from '@factorialco/f0-react'
import type { F0FormRef } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { TrainingRequestDetailFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'

import { buildHeaderDetails, DraftForm, RichTextSection } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailSidepanelComponent/shared'
import { useFromCatalogForm } from './form'

type Props = {
  trainingRequest: TrainingRequestDetailFragment
  id?: string
  isDraft?: boolean
  formRef?: MutableRefObject<F0FormRef | null>
  onSuccess?: () => void
  onFormReady?: (helpers: { setSendForApproval: (value: boolean) => void }) => void
}

export const FromCatalog = ({
  trainingRequest,
  isDraft = false,
  formRef,
  onSuccess,
  onFormReady,
}: Props) => {
  const headerDetails = buildHeaderDetails(
    trainingRequest,
    i18n.t('trainings.training_requests.create.button.from_catalog'),
    { isDraft }
  )

  if (isDraft) {
    return (
      <FromCatalogDraft
        trainingRequest={trainingRequest}
        headerDetails={headerDetails}
        formRef={formRef}
        onSuccess={onSuccess}
        onFormReady={onFormReady}
      />
    )
  }

  // Read-only mode
  return (
    <F0Box display="flex" flexDirection="column" gap="xl">
      <DetailsItemsList details={headerDetails} tableView />

      <RichTextSection
        label={i18n.t('trainings.training_requests.my_requests.detail.training_need')}
        content={trainingRequest.trainingNeed ?? ''}
        paddingBottom="xl"
      />

      <RichTextSection
        label={i18n.t('trainings.training_requests.my_requests.detail.additional_comments')}
        content={trainingRequest.comments ?? ''}
        paddingBottom="xl"
      />

      <ApprovalsInformationBlock
        resourceId={String(trainingRequest.id)}
        resourceType={'Trainings::TrainingRequest'}
        newLook
      />
    </F0Box>
  )
}

// --- Draft mode ---

const FromCatalogDraft = ({
  trainingRequest,
  headerDetails,
  formRef,
  onSuccess,
  onFormReady,
}: {
  trainingRequest: TrainingRequestDetailFragment
  headerDetails: ReturnType<typeof buildHeaderDetails>
  formRef?: MutableRefObject<F0FormRef | null>
  onSuccess?: () => void
  onFormReady?: (helpers: { setSendForApproval: (value: boolean) => void }) => void
}) => {
  const formHook = useFromCatalogForm({ trainingRequest, onSuccess })

  return (
    <DraftForm
      formName="from-catalog-training-request"
      formHook={formHook}
      headerDetails={headerDetails}
      formRef={formRef}
      onFormReady={onFormReady}
    />
  )
}
